import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, GiftIcon, InfoIcon, ScaleIcon, ShieldIcon, TicketIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { EmptyState } from '../../components/ui/EmptyState';
import { GiftTile } from '../../components/campagift/GiftTile';
import { GiftDetail } from '../../components/campagift/GiftDetail';
import { BuyGiftDialog } from '../../components/campagift/BuyGiftDialog';
import { useCampaGift } from '../../contexts/CampaGiftContext';
import { bpsLabel, daysUntilExpiry, koboToNaira } from '../../utils/campagift';
const TABS = ['My gifts', 'Buy a gift', 'Sent', 'How it works'] as const;
export function CampaGiftPage() {
  const {
    gifts,
    typeOf,
    remaining,
    status,
    handle,
    types,
    redemptions
  } = useCampaGift();
  const [tab, setTab] = useState<(typeof TABS)[number]>('My gifts');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [buyTypeId, setBuyTypeId] = useState<string | null>(null);
  const held = useMemo(() => gifts.filter(gift => gift.holderHandle === handle), [gifts, handle]);
  const sent = useMemo(() => gifts.filter(gift => gift.purchaserHandle === handle && gift.holderHandle !== handle), [gifts, handle]);
  const selected = held.find(gift => gift.id === selectedId) ?? held[0] ?? null;
  const activeValue = held.filter(gift => ['available', 'partially_redeemed'].includes(status(gift))).reduce((total, gift) => total + remaining(gift), 0);
  const expiringValue = held.filter(gift => {
    const days = daysUntilExpiry(gift);
    return days !== null && days > 0 && days <= 30 && remaining(gift) > 0;
  }).reduce((total, gift) => total + remaining(gift), 0);
  /* Reversed originals stay in the sum; their compensating entry cancels them. */
  const redeemedValue = redemptions.filter(r => held.some(g => g.id === r.giftId)).reduce((total, r) => total + r.amountKobo, 0);
  return <div className="space-y-6" data-id="element-10320" data-mp="yefjfpz02ete">
      <PageHeader eyebrow="Value instrument" title="CampaGift" description="Give someone value they can use inside the Campuzon economy. CampaGift is redeemable with participating merchants under its own rules — it is not cash and cannot be withdrawn." actions={<Button variant="primary" icon={<GiftIcon aria-hidden className="h-4 w-4" data-id="element-10323" data-mp="1h6qgv4avxkk4" />} onClick={() => {
      setTab('Buy a gift');
    }} data-id="element-10322" data-mp="1c8f68rcrn60u">
            Buy a CampaGift
          </Button>} data-id="element-10321" data-mp="ydcrdepxy6bw" />

      <div className="grid gap-4 sm:grid-cols-3" data-id="element-10324" data-mp="3iyd896yovhh4">
        <div className="rounded-card border border-brand-700 bg-surface px-5 py-4" data-id="element-10325" data-mp="3nw4y7vfrwtio">
          <p className="text-body-sm text-ink-muted" data-id="element-10326" data-mp="3nxzn1ku615s1">Redeemable now</p>
          <p className="cz-num mt-1.5 text-h1 font-semibold text-ink" data-id="element-10327" data-mp="252lczwqw0ora">
            {koboToNaira(activeValue)}
          </p>
          <p className="mt-1 text-caption text-ink-muted" data-id="element-10328" data-mp="208h6tfpg4kr9">
            Across {held.filter(g => remaining(g) > 0).length} active gift
            {held.filter(g => remaining(g) > 0).length === 1 ? '' : 's'}
          </p>
        </div>
        <div className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-10329" data-mp="2o064lbl1e1fl">
          <p className="text-body-sm text-ink-muted" data-id="element-10330" data-mp="2jykkb566akvd">Expiring within 30 days</p>
          <p className="cz-num mt-1.5 text-h2 font-semibold text-ink" data-id="element-10331" data-mp="2jwgicsru3vxq">
            {koboToNaira(expiringValue)}
          </p>
          <p className="mt-1 text-caption text-ink-muted" data-id="element-10332" data-mp="2a0mwgis86q3a">
            You are notified before a gift expires
          </p>
        </div>
        <div className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-10333" data-mp="13kuh00mvo0ms">
          <p className="text-body-sm text-ink-muted" data-id="element-10334" data-mp="ykoqinscgk6a">Redeemed to date</p>
          <p className="cz-num mt-1.5 text-h2 font-semibold text-ink" data-id="element-10335" data-mp="2mhwdtyw30g5m">
            {koboToNaira(redeemedValue)}
          </p>
          <p className="mt-1 text-caption text-ink-muted" data-id="element-10336" data-mp="2r9eesmy3ugql">Derived from redemption records</p>
        </div>
      </div>

      <div className="rounded-card border border-line bg-canvas px-5 py-4" data-id="element-10337" data-mp="2di9d7fb6fedm">
        <p className="flex items-start gap-2 text-body-sm text-ink-muted" data-id="element-10338" data-mp="2if9jnrbxuj59">
          <ScaleIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ink-subtle" data-id="element-10339" data-mp="365wscetohcsv" />
          <span data-id="element-10340" data-mp="3a78j4noag42n">
            CampaGift is a distinct instrument. It is not your{' '}
            <Link to="/money" className="font-medium text-brand-800 underline" data-id="element-10341" data-mp="3a54eu4mh931u">
              Campuzon money
            </Link>
            , not marketplace escrow and not a library entitlement — each is recorded separately and
            cannot be spent as another. Gift value is redeemable only where its rules permit.
          </span>
        </p>
      </div>

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="CampaGift sections" data-id="element-10342" data-mp="1ps6lx1f36cg2" />

      {tab === 'My gifts' && <>
          {held.length === 0 ? <EmptyState icon={<GiftIcon aria-hidden className="h-6 w-6" data-id="element-10344" data-mp="1d7zuqwphflq1" />} title="You have no CampaGifts yet" description="Gifts sent to your handle appear here as soon as they are issued." data-id="element-10343" data-mp="1d91fxu07polh" /> : <div className="grid gap-6 lg:grid-cols-[380px_minmax(0,1fr)]" data-id="element-10345" data-mp="1m7d5dspcegcc">
              <div className="space-y-3" data-id="element-10346" data-mp="ykest64cfsjp">
                {held.map(gift => <GiftTile key={gift.id} gift={gift} type={typeOf(gift)} remainingKobo={remaining(gift)} status={status(gift)} selected={selected?.id === gift.id} onSelect={() => setSelectedId(gift.id)} data-id="element-10347" data-mp="ujbbsgak7qgw" />)}
              </div>
              {selected && <GiftDetail gift={selected} data-id="element-10348" data-mp="atj1sb7pp1di" />}
            </div>}
        </>}

      {tab === 'Buy a gift' && <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-10349" data-mp="2aufy0c7ktaf2">
          {types.filter(type => type.enabled && type.funding === 'user_purchase').map(type => <article key={type.id} className="flex flex-col rounded-card border border-line bg-surface px-5 py-4" data-id="element-10350" data-mp="2kogaiz56thxt">
                <div className="flex flex-wrap items-start justify-between gap-2" data-id="element-10351" data-mp="1ryzmedolon7e">
                  <h3 className="text-h4 font-semibold text-ink" data-id="element-10352" data-mp="1wvzj3xzk8dlu">{type.name}</h3>
                  {type.purchaseFeeBps === 0 && <Badge tone="ok" plain data-id="element-10353" data-mp="21uki5u32sr8p">
                      No purchase fee
                    </Badge>}
                </div>
                <p className="mt-2 text-body-sm text-ink-muted" data-id="element-10354" data-mp="3dbk9amdot015">{type.description}</p>
                <dl className="mt-3 space-y-1.5 text-caption text-ink-muted" data-id="element-10355" data-mp="3hcmdlhmgo29z">
                  <div className="flex justify-between gap-3" data-id="element-10356" data-mp="3hep7icqqifjz">
                    <dt data-id="element-10357" data-mp="15nzby7gykicp">Value range</dt>
                    <dd className="cz-num text-ink" data-id="element-10358" data-mp="15r3l7j9a64a8">
                      {koboToNaira(type.minValueKobo)} – {koboToNaira(type.maxValueKobo)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-10359" data-mp="wuumk6t4n9p5">
                    <dt data-id="element-10360" data-mp="rx2w7h84rvn2">Purchase fee</dt>
                    <dd className="cz-num text-ink" data-id="element-10361" data-mp="rw1jl8bgv138">{bpsLabel(type.purchaseFeeBps)}</dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-10362" data-mp="hzoi3o9ojixx">
                    <dt data-id="element-10363" data-mp="hydcrk0l634p">Expiry</dt>
                    <dd className="text-ink" data-id="element-10364" data-mp="dy3aahc05uul">
                      {type.expiryDays ? `${type.expiryDays} days` : 'None'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-10365" data-mp="90uqydf4bkrr">
                    <dt data-id="element-10366" data-mp="28b2smi7rp35k">Transferable</dt>
                    <dd className="text-ink" data-id="element-10367" data-mp="28a1g09b3s8lq">
                      {type.transferability === 'transferable' ? 'Yes' : 'No'}
                    </dd>
                  </div>
                </dl>
                <div className="mt-4 border-t border-line pt-4" data-id="element-10368" data-mp="1c8gqaewtkob6">
                  <Button variant="primary" size="sm" block onClick={() => setBuyTypeId(type.id)} icon={<GiftIcon aria-hidden className="h-4 w-4" data-id="element-10370" data-mp="17g6wgnsl343m" />} data-id="element-10369" data-mp="1h5qg3vo4f1zj">
                    Buy {type.name}
                  </Button>
                </div>
              </article>)}
        </div>}

      {tab === 'Sent' && <>
          {sent.length === 0 ? <EmptyState icon={<TicketIcon aria-hidden className="h-6 w-6" data-id="element-10372" data-mp="jkko4uohe470" />} title="You have not sent a CampaGift yet" description="Gifts you buy for other people are listed here with their current status." data-id="element-10371" data-mp="jjt4mi85rwx3" /> : <Panel title="Gifts you sent" bodyClassName="p-0" data-id="element-10373" data-mp="2ivvhvaq6vv2b">
              <ul className="divide-y divide-line" data-id="element-10374" data-mp="2iu190us6ztel">
                {sent.map(gift => <li key={gift.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4" data-id="element-10375" data-mp="791qdpuiddhs">
                    <div className="min-w-0 flex-1" data-id="element-10376" data-mp="2bibcnpzkwos">
                      <p className="text-body font-medium text-ink" data-id="element-10377" data-mp="gytnortzskpy">
                        {typeOf(gift).name} to @{gift.holderHandle}
                      </p>
                      <p className="cz-num mt-0.5 text-caption text-ink-muted" data-id="element-10378" data-mp="c60nmh9gy0k7">
                        {gift.publicReference} · issued {gift.issuedAt} ·{' '}
                        {koboToNaira(remaining(gift))} of {koboToNaira(gift.faceValueKobo)} left
                      </p>
                    </div>
                    <Badge tone={remaining(gift) === 0 ? 'neutral' : 'brand'} plain data-id="element-10379" data-mp="uycunhb2hq0o">
                      {remaining(gift) === 0 ? 'Fully redeemed' : 'In use'}
                    </Badge>
                  </li>)}
              </ul>
              <p className="border-t border-line px-5 py-3 text-caption text-ink-muted" data-id="element-10380" data-mp="2wr1262uquzm9">
                You can see status and remaining value, but not where the holder shopped. Their
                redemption detail is theirs.
              </p>
            </Panel>}
        </>}

      {tab === 'How it works' && <div className="grid gap-6 lg:grid-cols-2" data-id="element-10381" data-mp="2nkhfnvwszh2z">
          <Panel title="From gift to merchant settlement" data-id="element-10382" data-mp="2ikd7ct61tsnu">
            <ol className="space-y-3" data-id="element-10383" data-mp="3g72efhflyvgk">
              {['You buy a CampaGift. Value is issued only after the payment provider confirms server-side.', 'The recipient is notified and opens it. Some gift types require them to accept first.', 'They present the QR code or redemption code at an eligible merchant.', 'Campuzon validates status, expiry, merchant, category and every configured limit before anything moves.', 'The merchant redeems part or all of the value. Remaining value is recalculated from the record.', 'A merchant receivable is created, less the configured commission.', 'Settlement pays the merchant through a licensed provider, and the ledger records it.'].map((line, index) => <li key={line} className="flex gap-3" data-id="element-10384" data-mp="fai7owxp3kp3" data-mpi={index}>
                  <span className="cz-num mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-line bg-canvas text-caption font-semibold text-ink-muted" data-id="element-10385" data-mp="fcukywsid6vt">
                    {index + 1}
                  </span>
                  <p className="text-body-sm text-ink-muted" data-id="element-10386" data-mp="t9rcz1kd4uea">{line}</p>
                </li>)}
            </ol>
          </Panel>
          <div className="space-y-6" data-id="element-10387" data-mp="y2194zc2mqp0">
            <Panel title="What CampaGift is not" data-id="element-10388" data-mp="1302trop5ay2l">
              <ul className="space-y-2.5 text-body-sm text-ink-muted" data-id="element-10389" data-mp="12zug3ul77oqc">
                <li className="flex items-start gap-2" data-id="element-10390" data-mp="1808fko8qhqcr">
                  <ShieldIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-10391" data-mp="17xm6gk6732sf" />
                  Not cash. It cannot be withdrawn, and it is not a bank balance.
                </li>
                <li className="flex items-start gap-2" data-id="element-10392" data-mp="n81qdjvhi221">
                  <ShieldIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-10393" data-mp="icmeuyky8i8s" />
                  Not your Campuzon money. The two are separate records with separate rules.
                </li>
                <li className="flex items-start gap-2" data-id="element-10394" data-mp="s7wjlu2qf3q9">
                  <ShieldIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-10395" data-mp="160cpr3qxjhve" />
                  Not a library entitlement. Redeeming a Library gift creates an entitlement; the
                  entitlement is what grants access, not the gift.
                </li>
              </ul>
            </Panel>
            <Panel title="Before real money flows" data-id="element-10396" data-mp="1b10f7kzifqni">
              <p className="flex items-start gap-2 text-body-sm text-ink-muted" data-id="element-10397" data-mp="1ay4ifzzef80n">
                <InfoIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-10398" data-mp="3hqos0f87pyz5" />
                Calling something a gift does not remove financial or regulatory obligations.
                Purchase, settlement and bulk issuance stay switched off until the stored-value,
                voucher, payments, tax and consumer-protection position is settled for each market.
              </p>
              <Link to="/money" className="mt-3 inline-block" data-id="element-10399" data-mp="1ij83si177ohn">
                <Button size="sm" variant="secondary" icon={<ArrowRightIcon aria-hidden className="h-4 w-4" data-id="element-10401" data-mp="1nghxi9vmemba" />} data-id="element-10400" data-mp="1nhjawl03zkw6">
                  See the capability register
                </Button>
              </Link>
            </Panel>
          </div>
        </div>}

      <BuyGiftDialog open={buyTypeId !== null} typeId={buyTypeId} onClose={() => setBuyTypeId(null)} data-id="element-10402" data-mp="zklzyvearja1" />
    </div>;
}